import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CommentService } from '../services/CommentService';
import { Comment } from '../models/Comment';

interface CommentContextType {
  comments: Comment[];
  loadComments: (opportunityId: string) => Promise<void>;
  refreshComments: () => Promise<void>;
  currentOpportunityId: string | null;
  setCurrentOpportunityId: (id: string | null) => void;
}

const CommentContext = createContext<CommentContextType | undefined>(undefined);

export const useComments = () => {
  const context = useContext(CommentContext);
  if (context === undefined) {
    throw new Error('useComments must be used within a CommentProvider');
  }
  return context;
};

interface CommentProviderProps {
  children: ReactNode;
}

export const CommentProvider: React.FC<CommentProviderProps> = ({ children }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [currentOpportunityId, setCurrentOpportunityId] = useState<string | null>(null);

  const loadComments = useCallback(async (opportunityId: string) => {
    try {
      const fetchedComments = await CommentService.getCommentsByOpportunityId(opportunityId);
      setComments(fetchedComments);
      setCurrentOpportunityId(opportunityId);
    } catch (error) {
      console.error('[CommentContext] Failed to load comments:', error);
    }
  }, []);

  const refreshComments = useCallback(async () => {
    if (!currentOpportunityId) {
      console.warn('[CommentContext] Cannot refresh comments: No current opportunity ID');
      return;
    }
    
    try {
      await loadComments(currentOpportunityId);
    } catch (error) {
      console.error('[CommentContext] Failed to refresh comments:', error);
    }
  }, [currentOpportunityId, loadComments]);

  const value = {
    comments,
    loadComments,
    refreshComments,
    currentOpportunityId,
    setCurrentOpportunityId
  };

  return <CommentContext.Provider value={value}>{children}</CommentContext.Provider>;
}; 